import { create } from 'zustand';

export interface ConfirmActionPayload {
  type: 'delete_unit' | 'remove_member';
  unitId: string;
  itemId?: string; // For member removals
}

interface OrgState {
  // UI State
  selectedUnitId: string | null;
  expandedNodeIds: Set<string>;
  selectedHierarchyId: string | null;
  editingUnitId: string | null;
  showAddMemberModal: boolean;
  showConfirmModal: boolean;
  confirmAction: ConfirmActionPayload | null;
  draggedUnitId: string | null;
  showCreateHierarchyModal: boolean;

  // Actions
  selectUnit: (unitId: string | null) => void;
  toggleNodeExpanded: (nodeId: string) => void;
  selectHierarchy: (hierarchyId: string) => void;
  setEditingUnit: (unitId: string | null) => void;
  setShowAddMemberModal: (show: boolean) => void;
  setConfirmAction: (action: ConfirmActionPayload | null) => void;
  setShowConfirmModal: (show: boolean) => void;
  setDraggedUnitId: (unitId: string | null) => void;
  setShowCreateHierarchyModal: (show: boolean) => void;
  clearOrgState: () => void;
}

export const useOrgStore = create<OrgState>((set) => ({
  selectedUnitId: null,
  expandedNodeIds: new Set(),
  selectedHierarchyId: null,
  editingUnitId: null,
  showAddMemberModal: false,
  showConfirmModal: false,
  confirmAction: null,
  draggedUnitId: null,
  showCreateHierarchyModal: false,

  selectUnit: (unitId: string | null) => set({ selectedUnitId: unitId }),

  toggleNodeExpanded: (nodeId: string) =>
    set((state) => {
      const newSet = new Set(state.expandedNodeIds);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return { expandedNodeIds: newSet };
    }),

  selectHierarchy: (hierarchyId: string) =>
    set({
      selectedHierarchyId: hierarchyId,
      selectedUnitId: null, // Clear selection when hierarchy changes
      expandedNodeIds: new Set(),
    }),

  setEditingUnit: (unitId: string | null) => set({ editingUnitId: unitId }),

  setShowAddMemberModal: (show: boolean) =>
    set({ showAddMemberModal: show }),

  setConfirmAction: (action: ConfirmActionPayload | null) =>
    set({ confirmAction: action }),

  setShowConfirmModal: (show: boolean) => set({ showConfirmModal: show }),

  setDraggedUnitId: (unitId: string | null) => set({ draggedUnitId: unitId }),

  setShowCreateHierarchyModal: (show: boolean) => set({ showCreateHierarchyModal: show }),

  clearOrgState: () =>
    set({
      selectedUnitId: null,
      expandedNodeIds: new Set(),
      selectedHierarchyId: null,
      editingUnitId: null,
      showAddMemberModal: false,
      showConfirmModal: false,
      confirmAction: null,
      draggedUnitId: null,
      showCreateHierarchyModal: false,
    }),
}));
